/**
 * Story 5.5 — abandonment-recovery analytics listener (+ optional v2 banner).
 *
 * FR26 (architecture.md:1149): theme cooperates with Shopify-managed
 * abandonment recovery by emitting `benvel:cart-updated` events. This file
 * subscribes to those events (Story 5.1 dispatcher) and forwards a
 * PII-free, consent-gated payload to:
 *   - Shopify.analytics.publish('benvel_cart_updated', payload)
 *   - window.dataLayer.push({event: 'benvel_cart_updated', ...}) if present.
 *
 * Banned fields (PII allowlist per architecture.md:1215):
 *   customer.*, address.*, cart.attributes.email|phone, cart.items[], order.id.
 * Allowed fields (include-only buildPayload):
 *   cart_token (first6+last4 — never the full token), item_count,
 *   total_price_cents, currency, source, ts_iso.
 *
 * Consent gate: Shopify.customerPrivacy.analyticsProcessingAllowed() on every
 * emission. Fail-closed if the API is missing (Habeas Data risk-aversion).
 *
 * Dedup: 500ms rolling window keyed on
 *   `${cart_token}:${item_count}:${total_price_cents}` — collapses Story 5.1
 *   dual-fire (product-form + drawer fire for the same ATC) to one emission.
 *
 * v2 banner (AC6): feature-gated on
 *   document.documentElement.dataset.cartRecoveryEnabled === 'true'.
 *   Default OFF. Heuristic = localStorage rolling timestamp; recovery window
 *   6h..14d. localStorage write skipped when consent rejected.
 *
 * No window globals. Singleton listener on `document`.
 */
(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  var DEDUP_MS = 500;
  var STORAGE_KEY = "benvel_last_cart_visit_ts";
  var lastSeen = new Map();

  function hashCartToken(token) {
    if (typeof token !== "string" || token.length < 10) return null;
    return token.slice(0, 6) + "..." + token.slice(-4);
  }

  function buildPayload(cart, source) {
    return {
      cart_token: hashCartToken(cart && cart.token),
      item_count: cart && typeof cart.item_count === "number" ? cart.item_count : null,
      total_price_cents: cart && typeof cart.total_price === "number" ? cart.total_price : null,
      currency: cart && typeof cart.currency === "string" ? cart.currency : null,
      source: typeof source === "string" ? source : "unknown",
      ts_iso: new Date().toISOString(),
    };
  }

  function isAnalyticsAllowed() {
    var s = window.Shopify;
    return !!(
      s &&
      s.customerPrivacy &&
      typeof s.customerPrivacy.analyticsProcessingAllowed === "function" &&
      s.customerPrivacy.analyticsProcessingAllowed() === true
    );
  }

  function isDuplicate(p) {
    var key =
      (p.cart_token || "no-token") +
      ":" +
      (p.item_count == null ? "?" : p.item_count) +
      ":" +
      (p.total_price_cents == null ? "?" : p.total_price_cents);
    var now = Date.now();
    var last = lastSeen.get(key);
    if (last != null && now - last < DEDUP_MS) return true;
    lastSeen.set(key, now);
    if (lastSeen.size > 32) {
      for (var entry of lastSeen) {
        if (now - entry[1] > DEDUP_MS * 4) lastSeen.delete(entry[0]);
      }
    }
    return false;
  }

  function emit(payload) {
    try {
      var s = window.Shopify;
      if (s && s.analytics && typeof s.analytics.publish === "function") {
        s.analytics.publish("benvel_cart_updated", payload);
      } else if (console && typeof console.debug === "function") {
        console.debug("[benvel-cart-recovery]", payload);
      }
    } catch (_) {
      /* fail-silent */
    }
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: "benvel_cart_updated",
          cart_token: payload.cart_token,
          item_count: payload.item_count,
          total_price_cents: payload.total_price_cents,
          currency: payload.currency,
          source: payload.source,
          ts_iso: payload.ts_iso,
        });
      }
    } catch (_) {
      /* fail-silent */
    }
  }

  function recoveryHeuristic() {
    if (!isAnalyticsAllowed()) return false; // no localStorage write w/o consent.
    try {
      var nowMs = Date.now();
      var raw = window.localStorage.getItem(STORAGE_KEY);
      window.localStorage.setItem(STORAGE_KEY, String(nowMs));
      if (!raw) return false;
      var prev = parseInt(raw, 10);
      if (!isFinite(prev)) return false;
      var hours = (nowMs - prev) / 3600000;
      return hours >= 6 && hours <= 336; // 6h..14d
    } catch (_) {
      return false;
    }
  }

  function maybeApplyBanner() {
    var root = document.documentElement;
    if (!root || root.dataset.cartRecoveryEnabled !== "true") return;
    if (!recoveryHeuristic()) return;
    var nodes = document.querySelectorAll("[data-cart-trust-line]");
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.add("cart-drawer__trust-line--recovered");
    }
  }

  function handleCartUpdated(evt) {
    var detail = (evt && evt.detail) || {};
    var payload = buildPayload(detail.cart || null, detail.source || "unknown");
    if (isDuplicate(payload)) return;
    if (!isAnalyticsAllowed()) return;
    emit(payload);
  }

  document.addEventListener("benvel:cart-updated", handleCartUpdated);
  document.addEventListener("benvel:cart-updated", maybeApplyBanner);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", maybeApplyBanner, { once: true });
  } else {
    maybeApplyBanner();
  }
})();
